import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  FormLabel,
  Spinner,
  Stack,
  Switch,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import MDEditor from '@uiw/react-md-editor';
import { useEffect, useRef, useState } from 'react';
import remarkBreaks from 'remark-breaks';
import { translate } from '../../../i18n';
import {
  CguSource,
  UserMembershipTypeDto,
} from '../../../models/user-membership/dto/user-membership.dto';
import { useGetCgu } from '../hooks/useGetCgu';
import { useGetOrganizationCgu } from '../../organization/hooks/useGetOrganizationCgu';
import { useUpdateMembershipTypeCgu } from '../hooks/useUpdateMembershipTypeCgu';

type Props = {
  membershipType: UserMembershipTypeDto;
};

/**
 * Per-type CGU override editor. Toggle controls override presence:
 *
 * - OFF: type falls back to the org-level CGU. Turning ON pre-fills the editor
 *   with a copy of the current org CGU (so operators tweak, not rewrite).
 * - ON: operator edits the override and saves. Save hits
 *   POST /organizations/:orgId/membership-types/:typeId/cgu.
 * - Toggling OFF while an override exists opens a confirmation that clears it.
 *
 * Isolated from the main AddUserMembershipTypeModal form — CGU edits go through
 * a dedicated endpoint, not the generic membership-type update path.
 */
export default function MembershipTypeCguOverrideSection({
  membershipType,
}: Props) {
  const toast = useToast();
  const subtleColor = useColorModeValue(
    'secondaryGray.600',
    'secondaryGray.400',
  );
  const editorColorMode = useColorModeValue<'light' | 'dark'>('light', 'dark');

  const orgId = membershipType.organizationId;
  const typeId = membershipType.id;

  // Resolved CGU from the user-facing endpoint — reveals whether an override is
  // currently in effect (source === MEMBERSHIP_TYPE) vs org fallback.
  const { data: resolvedCgu, isLoading: isLoadingResolved } = useGetCgu(
    orgId,
    typeId,
  );
  // Current org-level CGU (for the pre-fill hint + actual copy when enabling).
  const { data: orgCgu } = useGetOrganizationCgu(orgId);

  const { mutateAsync: saveCgu, isLoading: isSaving } =
    useUpdateMembershipTypeCgu(orgId, typeId);

  const hasOverride = resolvedCgu?.source === CguSource.MEMBERSHIP_TYPE;

  // Toggle state is decoupled from "has override" so the operator can enable
  // the editor with pre-fill before committing.
  const [overrideEnabled, setOverrideEnabled] = useState(hasOverride);
  const [buffer, setBuffer] = useState<string>('');
  const lastSyncedRef = useRef<string | null>(null);

  // Sync toggle when backend state arrives / changes.
  useEffect(() => {
    if (isLoadingResolved) return;
    setOverrideEnabled(hasOverride);
  }, [hasOverride, isLoadingResolved]);

  // Sync the editor buffer:
  //   - If override is in effect, buffer = the override's text.
  //   - If just enabled (no override yet), buffer = copy of org CGU so the
  //     operator tweaks rather than starts blank.
  useEffect(() => {
    if (isLoadingResolved) return;
    if (hasOverride && resolvedCgu) {
      if (lastSyncedRef.current === resolvedCgu.text) return;
      setBuffer(resolvedCgu.text);
      lastSyncedRef.current = resolvedCgu.text;
    } else if (overrideEnabled && !hasOverride) {
      // Transition: operator just enabled the toggle. Pre-fill with org CGU.
      const orgText = orgCgu?.cguText ?? '';
      if (lastSyncedRef.current === `__prefill__${orgText}`) return;
      setBuffer(orgText);
      lastSyncedRef.current = `__prefill__${orgText}`;
    } else if (!overrideEnabled && !hasOverride) {
      setBuffer('');
      lastSyncedRef.current = null;
    }
  }, [hasOverride, resolvedCgu?.text, orgCgu?.cguText, overrideEnabled, isLoadingResolved]);

  const removeDialog = useDisclosure();
  const removeCancelRef = useRef<HTMLButtonElement>(null);

  const savedText = hasOverride ? resolvedCgu?.text ?? '' : '';
  const isDirty = buffer !== savedText;
  const isEmpty = buffer.trim().length === 0;
  const canSave = overrideEnabled && isDirty && !isEmpty;

  const handleToggle = (next: boolean) => {
    if (!next && hasOverride) {
      // Going from "override ON" to "override OFF" with an existing override —
      // confirm before clearing.
      removeDialog.onOpen();
      return;
    }
    setOverrideEnabled(next);
  };

  const doSave = async () => {
    if (!canSave) return;
    try {
      await saveCgu({ cguText: buffer });
      lastSyncedRef.current = buffer;
      toast({
        title: translate('common.save'),
        status: 'success',
        duration: 2000,
        position: 'top-right',
      });
    } catch {
      toast({
        title: translate('common.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const doClear = async () => {
    try {
      await saveCgu({ cguText: null });
      setOverrideEnabled(false);
      setBuffer('');
      lastSyncedRef.current = null;
      toast({
        title: translate('cgu.override_cleared'),
        status: 'success',
        duration: 2000,
        position: 'top-right',
      });
    } catch {
      toast({
        title: translate('common.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      removeDialog.onClose();
    }
  };

  return (
    <>
      <Box borderTopWidth="1px" pt={4} mt={4}>
        <Stack spacing={3}>
          <Flex align="center" justify="space-between">
            <Box>
              <FormLabel mb={0}>{translate('cgu.override_title')}</FormLabel>
              <Text fontSize="xs" color={subtleColor}>
                {translate('cgu.override_description')}
              </Text>
            </Box>
            <Flex align="center" gap={2}>
              {hasOverride && (
                <Badge colorScheme="purple">
                  v{membershipType.cguVersion}
                </Badge>
              )}
              <Switch
                isChecked={overrideEnabled}
                isDisabled={isSaving || isLoadingResolved}
                onChange={(e) => handleToggle(e.target.checked)}
              />
            </Flex>
          </Flex>

          {overrideEnabled && (
            <>
              {isLoadingResolved ? (
                <Flex justify="center" py={6}>
                  <Spinner />
                </Flex>
              ) : (
                <Box data-color-mode={editorColorMode}>
                  <MDEditor
                    value={buffer}
                    onChange={(v) => setBuffer(v ?? '')}
                    height={320}
                    preview="live"
                    textareaProps={{
                      placeholder: translate('cgu.editor_placeholder'),
                      'aria-label': translate('cgu.override_title'),
                    }}
                    previewOptions={{ remarkPlugins: [remarkBreaks] }}
                  />
                </Box>
              )}

              <Text fontSize="xs" color={subtleColor}>
                {hasOverride
                  ? translate('cgu.override_edit_hint')
                  : translate('cgu.override_prefill_hint')}
              </Text>

              <Flex gap={3}>
                <Button
                  size="sm"
                  colorScheme="brand"
                  onClick={doSave}
                  isDisabled={!canSave}
                  isLoading={isSaving && !removeDialog.isOpen}
                >
                  {translate('common.save')}
                </Button>
                {isDirty && isEmpty && (
                  <Text fontSize="sm" color="red.500" alignSelf="center">
                    {translate('cgu.empty_save_blocked')}
                  </Text>
                )}
              </Flex>
            </>
          )}
        </Stack>
      </Box>

      <AlertDialog
        isOpen={removeDialog.isOpen}
        onClose={removeDialog.onClose}
        leastDestructiveRef={removeCancelRef}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              {translate('cgu.override_clear_title')}
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text>{translate('cgu.override_clear_body')}</Text>
              <Text mt={2} fontSize="sm" color={subtleColor}>
                {translate('cgu.override_clear_hint')}
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={removeCancelRef}
                onClick={removeDialog.onClose}
                isDisabled={isSaving}
              >
                {translate('common.cancel')}
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={doClear}
                isLoading={isSaving}
              >
                {translate('cgu.override_clear_confirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
